import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAccounts, fetchTags, getTagSummary } from '../api/endpointsBackend';

const ACCOUNT_PALETTE = [
  '#3B82F6', '#22C55E', '#F59E0B', '#06B6D4',
  '#8B5CF6', '#EC4899', '#10B981', '#EF4444',
  '#F97316', '#A855F7',
];

function colorForAccount(account, index) {
  if (account?.color) return account.color;
  return ACCOUNT_PALETTE[index % ACCOUNT_PALETTE.length];
}

/**
 * Shape one tag's server-computed summary for the UI (MI-316).
 *
 * The figures arrive already netted — spent counts money that left the user's
 * own accounts, i_owe / owed_to_me carry the debt position. Nothing is summed
 * or absolute-valued here: doing that client-side is what double-counted every
 * transfer's two legs, and the movement list is paginated anyway.
 */
function shapeSummary(summary, accountsById, currencyFilter) {
  const currencies = (summary?.by_currency || []).filter(
    (c) => !currencyFilter || currencyFilter === 'all' || c.currency === currencyFilter
  );

  const byCurrency = {};
  for (const c of currencies) {
    byCurrency[c.currency] = {
      currency: c.currency,
      spent: c.spent || 0,
      borrowed: c.borrowed || 0,
      repaid: c.repaid || 0,
      iOwe: c.i_owe || 0,
      owedToMe: c.owed_to_me || 0,
      byAccount: (c.by_account || []).map((a, i) => {
        const acct = accountsById.get(a.account_id);
        return {
          accountId: a.account_id,
          name: acct?.name || `Account ${a.account_id}`,
          color: colorForAccount(acct, i),
          spent: a.spent || 0,
        };
      }),
    };
  }

  const totals = Object.values(byCurrency);
  return {
    byCurrency,
    // A tag with any debt leg gets the progress block; one with plain spending
    // gets the per-account block. Both can be true at once.
    hasDebt: totals.some((c) => c.borrowed > 0 || c.repaid > 0),
    hasSpend: totals.some((c) => c.spent > 0),
    grandSpent: totals.reduce((s, c) => s + c.spent, 0),
  };
}

export function useTagsView({ selectedCurrency = 'all' } = {}) {
  const [tags, setTags] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summaryByTag, setSummaryByTag] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tagsResp, accountsResp] = await Promise.all([
        fetchTags(null),
        fetchAccounts(),
      ]);
      const tagList = tagsResp || [];
      setTags(tagList);
      setAccounts(accountsResp || []);

      const results = await Promise.all(
        tagList.map((t) =>
          getTagSummary(t.id)
            .then((summary) => [t.id, summary])
            .catch(() => [t.id, null])
        )
      );
      const map = {};
      for (const [id, summary] of results) map[id] = summary;
      setSummaryByTag(map);
    } catch (err) {
      console.error('Error loading tags view:', err);
      setError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const accountsById = useMemo(() => {
    const m = new Map();
    accounts.forEach((a) => m.set(a.id, a));
    return m;
  }, [accounts]);

  const tabCurrencies = useMemo(() => {
    const set = new Set();
    Object.values(summaryByTag).forEach((s) =>
      (s?.by_currency || []).forEach((c) => set.add(c.currency))
    );
    return Array.from(set).sort();
  }, [summaryByTag]);

  const rows = useMemo(() => {
    return tags.map((tag) => ({
      ...tag,
      ...shapeSummary(summaryByTag[tag.id], accountsById, selectedCurrency),
    }));
  }, [tags, summaryByTag, accountsById, selectedCurrency]);

  return {
    loading,
    error,
    rows,
    accountsById,
    tabCurrencies,
    refetch,
  };
}
